import { openDialog } from '@patrianna/dialog-manager'
import { getTranslationFromJson } from '@patrianna/shared-utils/prismic'
import Link from 'next/link'
import { useState } from 'react'

import Loader from 'components/Loader'
import Page from 'components/Page'
import ROUTES from 'config/routes.json'
import LogInToAccess from 'pageContainers/LogInToAccess'
import PageNotAvailable from 'pageContainers/PageNotAvailable'
import ScratchCardStep from 'pageContainers/PostalCodePage/components/ScratchCardStep'
import content from 'prismic-translates/dialog-sc-request-code.json'
import Recaptcha from 'src/components/Recaptcha'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import { sweepstakeEnabledSelector } from 'store/modules/appConfig/selectors'
import { getKycStatusSelector } from 'store/modules/kyc/selectors'
import { openSnackbar } from 'store/modules/snackbar/actions'
import { isLoggedInSelector } from 'store/modules/user/selectors'

import ButtonGetRequest from './components/ButtonGetRequest'
import RecivedCode from './components/RecivedCode'
import classes from './styles.module.scss'
import { PostalCodeStepsEnum } from './types'
import { useSCRequest } from './utils'

const t = getTranslationFromJson([content])

export default function PostalCodePage() {
  const dispatch = useAppDispatch()

  const loggedIn = useAppSelector(isLoggedInSelector)
  const isSCenabled = useAppSelector(sweepstakeEnabledSelector)
  const kycStatus = useAppSelector(getKycStatusSelector)

  const needKycVerification = ['id_confirmed', 'initial'].includes(kycStatus)
  const isKycConfirmed = kycStatus === 'confirmed'

  const [step, setStep] = useState<PostalCodeStepsEnum>(PostalCodeStepsEnum.REQUEST_CODE_STEP_1)
  const { SCCode, isSCLoading, setRecaptchaToken, requestSCTokenWithRevealToken } = useSCRequest()

  const goToRecaptchaStep = () => {
    if (needKycVerification) {
      openDialog({
        dialogName: 'GET_VERIFIED_DIALOG',
        dialogProps: { verificationFrom: 'sweepstakes-code' },
      })
    } else if (isKycConfirmed) {
      setStep(PostalCodeStepsEnum.RECAPCHA_STEP_2)
    } else {
      dispatch(openSnackbar({ message: t('contact_support') }))
    }
  }

  const onScratchComplete = () => {
    setStep(PostalCodeStepsEnum.LOADING)
    requestSCTokenWithRevealToken(setStep)
  }

  const getScCodeResponse = (token: string) => {
    setStep(PostalCodeStepsEnum.LOADING)
    setRecaptchaToken(token, setStep)
  }

  const renderStep = (currentStep: PostalCodeStepsEnum) => {
    switch (currentStep) {
      case PostalCodeStepsEnum.REQUEST_CODE_STEP_1:
        return <ButtonGetRequest goToRecaptchaStep={goToRecaptchaStep} />

      case PostalCodeStepsEnum.RECAPCHA_STEP_2:
        return <Recaptcha verifyCallback={getScCodeResponse} source='sweepstakes' />

      case PostalCodeStepsEnum.SCRATCH_STEP_3:
        return <ScratchCardStep onScratchComplete={onScratchComplete} />

      case PostalCodeStepsEnum.RECEIVED_CODE_STEP_4:
        return <RecivedCode scCode={SCCode} isLoading={isSCLoading} />

      case PostalCodeStepsEnum.LOADING:
        return <Loader />

      default:
        return null
    }
  }

  if (!loggedIn) {
    return (
      <div className={classes.page}>
        <LogInToAccess />
      </div>
    )
  }

  if (!isSCenabled) {
    return (
      <div className={classes.page}>
        <PageNotAvailable />
      </div>
    )
  }

  return (
    <Page className={classes.root}>
      <h5 className={`mt-typography-h5 ${classes.title}`}>
        {t('currency')} {t('title')}
      </h5>
      <div className={classes.content}>{renderStep(step)}</div>
      <p className='mt-typography-caption'>
        {t('footer')}{' '}
        <Link href={ROUTES.SWEEPSTAKE_RULES} prefetch={false}>
          {t('sc_rules')}
        </Link>
      </p>
    </Page>
  )
}
